import { useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { getUserDisplayName, useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

/**
 * Get initials from name or email
 */
const getInitials = (nameOrEmail: string): string => {
  const trimmed = nameOrEmail.trim();

  if (trimmed.includes('@')) {
    const localPart = trimmed.split('@')[0];
    return localPart.slice(0, 2).toUpperCase();
  }

  const words = trimmed.split(/\s+/);

  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  return trimmed.slice(0, 2).toUpperCase();
};

export const useSimpleUsers = (
  nameOrEmail: string | string[] | null | undefined
) => {
  const displayName = useMemo(() => {
    if (!nameOrEmail) return null;
    if (Array.isArray(nameOrEmail)) {
      return nameOrEmail.filter(Boolean).join(', ') || null;
    }
    return nameOrEmail;
  }, [nameOrEmail]);

  const initials = displayName ? getInitials(displayName) : '';
  return { initials, displayName, isTeam: false };
};

/**
 * Hook for context-based users (from allActiveUserInfos)
 * Handles array of user IDs
 */
export const useContextBasedUsers = (
  userIds: string[] | string | null | undefined
) => {
  const { allActiveUserInfos } = useSellerAccountContext();
  const hasUserTeams = useUserHasFeature(Feature.UserTeams);

  return useMemo(() => {
    const ids = Array.isArray(userIds) ? userIds : userIds ? [userIds] : [];

    const displayName = ids.length
      ? [...new Set(ids)]
          .map((userId) => allActiveUserInfos?.[userId]?.displayName)
          .filter((name) => name != null)
          .join(', ')
      : null;

    const initials = displayName ? getInitials(displayName) : '';
    const isTeam = Boolean(
      hasUserTeams && ids.some((id) => allActiveUserInfos?.[id]?.isTeam)
    );

    return { displayName, initials, isTeam };
  }, [allActiveUserInfos, hasUserTeams, userIds]);
};

/**
 * Hook for API-fetched users (using useGetUserInfos)
 * Handles single user ID, includes deactivated status
 */
export const useApiFetchedUser = (
  userId: string | string[] | null | undefined
) => {
  const deactivatedText = useContent(ContentId.Deactivated);
  const singleUserId = Array.isArray(userId) ? userId[0] : userId;
  const getUserInfosQuery = useGetUserInfos(singleUserId ? [singleUserId] : []);

  return useMemo(() => {
    const user = singleUserId
      ? getUserInfosQuery.data?.[singleUserId]
      : undefined;

    const displayName = user ? getUserDisplayName(user, deactivatedText) : null;
    const initials = displayName ? getInitials(displayName) : '';

    return { displayName, initials, isTeam: false };
  }, [deactivatedText, getUserInfosQuery.data, singleUserId]);
};

/**
 * Combined hook - tries context first, fetches missing users from API
 * Optimized to only fetch users not in context
 */
export const useCombinedUsers = (
  userIds: string[] | string | null | undefined
) => {
  const { allActiveUserInfos } = useSellerAccountContext();
  const deactivatedText = useContent(ContentId.Deactivated);
  const hasUserTeams = useUserHasFeature(Feature.UserTeams);

  // Normalize to array
  const idsArray = useMemo(() => {
    return Array.isArray(userIds) ? userIds : userIds ? [userIds] : [];
  }, [userIds]);

  // Find users not in context
  const missingUserIds = useMemo(() => {
    return idsArray.filter((userId) => !allActiveUserInfos?.[userId]);
  }, [idsArray, allActiveUserInfos]);

  // Fetch missing users from API
  const getUserInfosQuery = useGetUserInfos(
    missingUserIds.length > 0 ? missingUserIds : []
  );

  // Combine context + API data
  return useMemo(() => {
    const displayNames = [...new Set(idsArray)]
      .map((userId) => {
        // Try context first
        let displayName = allActiveUserInfos?.[userId]?.displayName;

        // Fall back to fetched data
        if (!displayName && getUserInfosQuery.data?.[userId]) {
          displayName = getUserDisplayName(
            getUserInfosQuery.data[userId],
            deactivatedText
          );
        }

        return displayName;
      })
      .filter((name) => name != null);

    const displayName = displayNames.length ? displayNames.join(', ') : null;
    const initials = displayName ? getInitials(displayName) : '';
    const isTeam = Boolean(
      hasUserTeams &&
      [...new Set(idsArray)].some((id) => allActiveUserInfos?.[id]?.isTeam)
    );

    return { displayName, initials, isTeam };
  }, [
    idsArray,
    allActiveUserInfos,
    hasUserTeams,
    getUserInfosQuery.data,
    deactivatedText,
  ]);
};
